import { API, ShareAPI } from '../../API';
import { platformNamesMapping } from '../../utils/platforms';
import { formatWithCustomGigaBillion } from '../../utils/fomatNumbers';

export type DistributionMode = 'publications' | 'likes';

interface BaseDistributionData {
  platform: string;
}

interface PublicationsDistributionItem extends BaseDistributionData {
  messages_count: number;
  messages_share: number;
}

interface LikesDistributionItem extends BaseDistributionData {
  likes_count: number;
  likes_share: number;
}

export interface DistributionDataResponse {
  objects: (PublicationsDistributionItem | LikesDistributionItem)[];
}

export interface DistributionChartConfig {
  mode: DistributionMode;
  widgetName: string;
  endpoint: string;
  countKey: string;
  shareKey: string;
  chartLabel: string;
}

const distributionConfigs: Record<DistributionMode, Omit<DistributionChartConfig, 'mode'>> = {
  publications: {
    widgetName: 'publications_by_platform_distribution_chart',
    endpoint: '/API/v2/narrative-widgets/{{narrativeId}}/platform-messages',
    countKey: 'messages_count',
    shareKey: 'messages_share',
    chartLabel: 'Publications',
  },
  likes: {
    widgetName: 'likes_by_platform_distribution_chart',
    endpoint: '/API/v2/narrative-widgets/{{narrativeId}}/platform-likes',
    countKey: 'likes_count',
    shareKey: 'likes_share',
    chartLabel: 'Likes',
  },
};

export const getDistributionConfig = (mode: DistributionMode): DistributionChartConfig => {
  return {
    mode,
    ...distributionConfigs[mode],
  };
};

export const fetchDistributionData = async (
  narrativeId: string | number,
  mode: DistributionMode,
  isShare: boolean = false,
): Promise<DistributionDataResponse> => {
  const config = getDistributionConfig(mode);
  const api = isShare ? ShareAPI : API;
  const urlParams = new URLSearchParams();
  urlParams.set('widget_name', config.widgetName);

  const endpoint = config.endpoint.replace('{{narrativeId}}', String(narrativeId));
  const url = `${endpoint}?${urlParams.toString()}`;

  return await api.fetch('GET', url);
};

export interface DistributionChartDatum {
  /** Display label: localized platform name. */
  name: string;
  value: number;
  /** Raw platform key, used to open the messages modal for the clicked slice. */
  platform: string;
  /** Pre-formatted count (e.g. "1.2K") for slice labels and tooltips. */
  formattedCount: string;
}

export const formatDistributionChartData = (
  data: DistributionDataResponse | null,
  config: DistributionChartConfig,
): DistributionChartDatum[] => {
  if (!data || !data.objects || data.objects.length === 0) {
    return [];
  }

  return data.objects.map((item) => {
    const count = Number((item as Record<string, unknown>)[config.countKey]) || 0;

    return {
      name: platformNamesMapping[item.platform] || item.platform,
      value: count,
      platform: item.platform,
      formattedCount: formatWithCustomGigaBillion(count),
    };
  });
};
